import type { CountryCode } from 'libphonenumber-js'
import { BuyNumberStep } from '../types'

export interface CountryFlowConfig {
  countryCode: CountryCode
  displayName: string
  features: {
    complianceRequired: boolean
  }
  stepSequence: BuyNumberStep[] // Ordered list of steps
}

// Centralized step labels - same across all countries
export const STEP_LABELS: Record<BuyNumberStep, string> = {
  [BuyNumberStep.SELECT_NUMBER]: 'Select Number',
  [BuyNumberStep.COMPLIANCE]: 'KYC',
  [BuyNumberStep.CONFIGURATION]: 'Configuration',
}

export const getStepLabel = (step: BuyNumberStep): string => STEP_LABELS[step]

export const COUNTRY_FLOW_CONFIGS: Partial<Record<CountryCode, CountryFlowConfig>> = {
  US: {
    countryCode: 'US',
    displayName: 'United States',
    features: {
      complianceRequired: false,
    },
    stepSequence: [BuyNumberStep.SELECT_NUMBER, BuyNumberStep.CONFIGURATION],
  },
  IN: {
    countryCode: 'IN',
    displayName: 'India',
    features: {
      complianceRequired: true,
    },
    // Compliance is chosen first: a number can't be selected without it, and a
    // 140/160 compliance pins the number search to the same series.
    stepSequence: [
      BuyNumberStep.COMPLIANCE,
      BuyNumberStep.SELECT_NUMBER,
      BuyNumberStep.CONFIGURATION,
    ],
  },
  // Example of country with no number selection (e.g., Brazil)
  // BR: {
  //   countryCode: 'BR',
  //   displayName: 'Brazil',
  //   features: {
  //     complianceRequired: true,
  //   },
  //   stepSequence: [BuyNumberStep.COMPLIANCE, BuyNumberStep.CONFIGURATION],
  // },
}

/**
 * Get the flow configuration for a given country.
 * Defaults to US flow if country not found.
 */
export const getCountryFlowConfig = (country: CountryCode): CountryFlowConfig => {
  const config = COUNTRY_FLOW_CONFIGS[country] || COUNTRY_FLOW_CONFIGS.US
  if (!config) {
    throw new Error('Country flow config for US must be defined as default')
  }
  return config
}

// Re-rent (buy-back of a previously owned number) skips these steps: the number
// is already chosen on the Unrented page, and compliance isn't required to
// reclaim a number — it carries its prior compliance and can be re-linked from
// the number's Configure screen if needed.
const RERENT_SKIPPED_STEPS: BuyNumberStep[] = [
  BuyNumberStep.SELECT_NUMBER,
  BuyNumberStep.COMPLIANCE,
]

/**
 * The steps a flow actually visits. Deriving this once keeps the next-step,
 * breadcrumbs, and initial landing consistent across every navigator.
 */
export const getEffectiveStepSequence = (
  config: CountryFlowConfig,
  { isRerent }: { isRerent: boolean },
): BuyNumberStep[] =>
  isRerent
    ? config.stepSequence.filter((step) => !RERENT_SKIPPED_STEPS.includes(step))
    : config.stepSequence

export const ALLOWED_COUNTRIES: CountryCode[] = [
  'US',
  'CA',
  'IN',
  'AE',
  'BR',
  'AU',
  'NZ',
  'GB',
  'SG',
]

type NumberType = 'local' | 'tollfree' | 'mobile'

const NUMBER_TYPE_CONFIG: Partial<Record<CountryCode, NumberType[]>> = {
  IN: ['local'],
  US: ['local', 'tollfree'],
  AU: ['local', 'tollfree', 'mobile'],
  GB: ['local', 'mobile'],
  SG: ['local'],
}

export const NUMBER_TYPE_OPTIONS = [
  { label: 'Local', value: 'local' },
  { label: 'Toll-Free', value: 'tollfree' },
  { label: 'Mobile', value: 'mobile' },
] as const

/**
 * Get the number type options for a given country.
 * Defaults to US number type options if country not found.
 */
export const getNumberTypeOptions = (country: CountryCode) => {
  const config = NUMBER_TYPE_CONFIG[country] || NUMBER_TYPE_CONFIG.US
  return NUMBER_TYPE_OPTIONS.filter((option) => config?.includes(option.value))
}

// India-specific number prefix options. Label is the trunk-prefixed form users
// recognise; value drops the leading 0 because Plivo matches against the E.164
// national portion (Bangalore = 9180…, Mumbai = 9122…).
export const INDIA_PREFIX_OPTIONS = [
  { label: '080', value: '80' },
  { label: '022', value: '22' },
] as const

// 160 is not buyable by search (it goes through compliance), so it is not an option.
export const getNumberPrefixOptions = (country: CountryCode) =>
  country === 'IN' ? [...INDIA_PREFIX_OPTIONS] : []
